import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { cacheApi } from '../utils/api'

const PAGE_TITLES: Record<string, string> = {
  '/articles': 'Content Manager',
  '/media': 'Media Gallery',
  '/redirects': 'SEO Redirects',
  '/flags': 'Feature Flags',
}

type Toast = { type: 'success' | 'error'; message: string } | null

export default function TopNav() {
  const location = useLocation()
  const [purging, setPurging] = useState(false)
  const [toast, setToast] = useState<Toast>(null)

  const title = Object.entries(PAGE_TITLES).find(([path]) =>
    location.pathname.startsWith(path)
  )?.[1] ?? 'Dashboard'

  function showToast(type: 'success' | 'error', message: string) {
    setToast({ type, message })
    setTimeout(() => setToast(null), 4000)
  }

  async function handlePurge() {
    if (purging) return
    setPurging(true)
    try {
      await cacheApi.purge()
      showToast('success', '✓ Edge cache purged across all PoPs')
    } catch (err) {
      showToast('error', `✗ ${err instanceof Error ? err.message : 'Purge failed'}`)
    } finally {
      setPurging(false)
    }
  }

  return (
    <>
      <header style={{
        height: 'var(--topnav-height)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 32px',
        background: 'rgba(9, 20, 19, 0.85)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: '1px solid var(--border)',
        position: 'sticky',
        top: 0,
        zIndex: 90,
        flexShrink: 0,
      }}>
        {/* Page Title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h1 style={{
            fontSize: '1.1rem',
            fontWeight: 600,
            color: 'var(--text-primary)',
            letterSpacing: '-0.02em',
          }}>
            {title}
          </h1>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Purge Cache Button */}
          <button
            id="btn-purge-cache"
            className="btn btn-ghost btn-sm"
            onClick={handlePurge}
            disabled={purging}
            title="Purge Cloudflare edge cache across all PoPs"
            style={{
              borderColor: purging ? 'var(--accent-border)' : undefined,
              color: purging ? 'var(--accent)' : undefined,
            }}
          >
            {purging ? (
              <>
                <span style={{
                  width: 12,
                  height: 12,
                  border: '2px solid var(--accent)',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  display: 'inline-block',
                  animation: 'spin 0.8s linear infinite',
                }} />
                Purging…
              </>
            ) : (
              <>⚡ Purge Edge Cache</>
            )}
          </button>

          {/* Status dot */}
          <div style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: 'var(--success)',
            boxShadow: '0 0 8px rgba(74,222,128,0.5)',
          }} />
        </div>
      </header>

      {/* Toast Notification */}
      {toast && (
        <div
          className={`toast toast-${toast.type}`}
          role="alert"
          aria-live="polite"
        >
          {toast.message}
        </div>
      )}
    </>
  )
}
